import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";

import {
  DEFAULT_ORDER,
  useMetricsSettingsStore,
  type MetricCardId
} from "@/store/metrics-settings-store";

/**
 * User-facing name for each metric card. Mirrors the labels rendered in
 * MetricsPanel — keep in sync if labels change there.
 */
const CARD_LABELS: Record<MetricCardId, string> = {
  weeklyTwr: "Weekly TWR",
  previousDay: "Previous Day / Today",
  weightedApy: "Weighted APY",
  ytd: "YTD",
  pastMonth: "Past Month",
  allTimeReturn: "All-time return",
  cash: "Cash / Buying Power",
  netDeposits: "Net Deposits",
  stocks: "Stocks",
  holdings: "Holdings",
  bots: "Bots",
  maxDrawdown: "Max drawdown",
  sharpe: "Sharpe ratio",
  maxRecovery: "MRec (max recovery time)"
};

/**
 * MetricsSettingsDialog — manages which metric cards are visible and
 * their display order, writing through `useMetricsSettingsStore`.
 *
 * Drag-and-drop is implemented with pointer events (no external DnD
 * library) to keep the bundle lean. The row the user is dragging gets
 * an `isDragging` visual; while dragging, hovering another row sets
 * that row as the drop target. On pointer-up we commit the new index
 * into the store. `pointerCapture` ensures we keep getting events even
 * when the cursor leaves the original row.
 */
export function MetricsSettingsDialog({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const theme = useTheme();
  const order = useMetricsSettingsStore((s) => s.order);
  const hidden = useMetricsSettingsStore((s) => s.hidden);
  const reorder = useMetricsSettingsStore((s) => s.reorder);
  const toggleHidden = useMetricsSettingsStore((s) => s.toggleHidden);
  const reset = useMetricsSettingsStore((s) => s.reset);
  const showRangeMetrics = useMetricsSettingsStore((s) => s.showRangeMetrics);
  const setShowRangeMetrics = useMetricsSettingsStore(
    (s) => s.setShowRangeMetrics
  );

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);

  // Pointer-driven drag state. `draggingId` names the row being moved;
  // `overId` names the row it would land before when dropped. Null
  // means no drag in progress.
  const [draggingId, setDraggingId] = useState<MetricCardId | null>(null);
  const [overId, setOverId] = useState<MetricCardId | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Derived: final ordering, filling in any ids missing from the
  // persisted order by appending defaults (defence in depth — the
  // store's `merge` hook already handles this, but newly-added cards
  // in a single session wouldn't have gone through merge yet).
  const displayOrder = useMemo<MetricCardId[]>(() => {
    const seen = new Set(order);
    const tail = DEFAULT_ORDER.filter((id) => !seen.has(id));
    return [...order, ...tail];
  }, [order]);

  const onDragStart = useCallback(
    (id: MetricCardId) => (e: React.PointerEvent<HTMLDivElement>) => {
      // Only respond to primary pointer (left-click or single touch).
      if (e.button !== 0 && e.pointerType === "mouse") return;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      setDraggingId(id);
      setOverId(id);
    },
    []
  );

  const onDragMove = useCallback(
    (id: MetricCardId) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingId) return;
      // Figure out which row the cursor is over by walking the list.
      // We hit-test against each rendered row's bounding rect; this is
      // cheap with ≤ 13 rows and avoids re-binding listeners per row.
      const container = listRef.current;
      if (!container) return;
      const y = e.clientY;
      const rows = Array.from(
        container.querySelectorAll<HTMLElement>("[data-metric-row]")
      );
      for (const row of rows) {
        const rect = row.getBoundingClientRect();
        if (y >= rect.top && y <= rect.bottom) {
          const rowId = row.dataset.metricRow as MetricCardId | undefined;
          if (rowId) setOverId(rowId);
          return;
        }
      }
      // Cursor above the first row or below the last → snap to the
      // nearest edge.
      if (rows.length > 0) {
        const first = rows[0]!.getBoundingClientRect();
        const lastEl = rows[rows.length - 1]!;
        const lastRect = lastEl.getBoundingClientRect();
        if (y < first.top) setOverId(rows[0]!.dataset.metricRow as MetricCardId);
        else if (y > lastRect.bottom) setOverId(lastEl.dataset.metricRow as MetricCardId);
      }
      // Unused parameter — silence lint without introducing a separate
      // prefix convention for event handlers.
      void id;
    },
    [draggingId]
  );

  const onDragEnd = useCallback(() => {
    if (draggingId && overId && draggingId !== overId) {
      const toIndex = displayOrder.indexOf(overId);
      if (toIndex >= 0) reorder(draggingId, toIndex);
    }
    setDraggingId(null);
    setOverId(null);
  }, [draggingId, overId, displayOrder, reorder]);

  // If the dialog closes mid-drag, make sure we clear state so the
  // next open starts fresh.
  useEffect(() => {
    if (!open) {
      setDraggingId(null);
      setOverId(null);
    }
  }, [open]);

  const rowBg = theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.04)
    : alpha(theme.palette.common.black, 0.03);
  const rowBgDrag = theme.palette.mode === "dark"
    ? alpha(theme.palette.primary.main, 0.12)
    : alpha(theme.palette.primary.main, 0.08);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pr: 5 }}>
        Manage metrics
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Toggle a card to show or hide it. Drag the handle to reorder.
        </Typography>
        {/* Range-scoped metrics opt-in. Sergey 2026-04-30: when ON
         *  and the chart is on a non-ALL range, the metrics panel
         *  prepends a "Range" section with Range Return / APY (if
         *  ≥ 90 days) / Max DD / Sharpe / YTD scoped to the active
         *  window. When OFF the section never renders. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 1,
            mb: 1.25,
            borderRadius: 1.5,
            bgcolor: rowBg
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Show metrics for selected range
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Adds a Range section above the cards when a non-ALL range is active.
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={showRangeMetrics}
            onChange={(_e, v) => setShowRangeMetrics(v)}
            inputProps={{ "aria-label": "Toggle range-scoped metrics" }}
          />
        </Box>
        <Box ref={listRef}>
          {displayOrder.map((id) => {
            const isDragging = draggingId === id;
            const isOver = draggingId && overId === id && draggingId !== id;
            const isHidden = hiddenSet.has(id);
            return (
              <Box
                key={id}
                data-metric-row={id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 1,
                  mb: 0.75,
                  borderRadius: 1.5,
                  bgcolor: isDragging || isOver ? rowBgDrag : rowBg,
                  opacity: isHidden ? 0.6 : 1,
                  transition: "background-color 120ms ease, opacity 120ms ease",
                  // Highlight the drop target with a top border while
                  // something else is being dragged over it.
                  borderTop: isOver
                    ? `2px solid ${theme.palette.primary.main}`
                    : "2px solid transparent"
                }}
              >
                <Box
                  role="button"
                  aria-label={`Drag to reorder ${CARD_LABELS[id]}`}
                  onPointerDown={onDragStart(id)}
                  onPointerMove={onDragMove(id)}
                  onPointerUp={onDragEnd}
                  onPointerCancel={onDragEnd}
                  sx={{
                    cursor: isDragging ? "grabbing" : "grab",
                    display: "flex",
                    alignItems: "center",
                    color: "text.secondary",
                    // Give the handle a bigger hit target than the
                    // icon so fingers can grab it on touch devices.
                    p: 0.5,
                    touchAction: "none"
                  }}
                >
                  <DragIndicatorRounded fontSize="small" />
                </Box>
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                  {CARD_LABELS[id]}
                </Typography>
                <Switch
                  size="small"
                  checked={!isHidden}
                  onChange={() => toggleHidden(id)}
                  inputProps={{
                    "aria-label": `${isHidden ? "Show" : "Hide"} ${CARD_LABELS[id]}`
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          size="small"
          startIcon={<RestartAltRounded />}
          onClick={() => reset()}
          color="inherit"
        >
          Reset
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
